import * as React from "react";
import { Alert, Card, CardContent, FormControlLabel, Stack, Switch, Typography } from "@mui/material";
import { getPreferences, updatePreferences } from "../services/api/notifications";

const LEAGUE_TOGGLES = [
  { field: "remindersEnabled", label: "Game reminders", hint: "A heads-up before games you're signed up for." },
  { field: "scheduleChangesEnabled", label: "Schedule changes", hint: "New times, rinks or other changes to your games." },
  { field: "announcementsEnabled", label: "League announcements", hint: "Messages from the organizers." },
];

/**
 * Profile > Notifications. Lets a signed-in person control which emails they get, per league,
 * and turn email off entirely. Changing these never affects registration or league access.
 */
export default function NotificationPreferences() {
  const [prefs, setPrefs] = React.useState(null);
  const [loadError, setLoadError] = React.useState("");
  const [saveError, setSaveError] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    getPreferences()
      .then((data) => {
        if (!cancelled) setPrefs(data);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : "Could not load your email settings.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Applies a change right away and puts the old values back if saving fails.
  const save = async (optimistic, changes) => {
    const previous = prefs;
    setPrefs(optimistic);
    setSaveError("");
    setSaving(true);
    try {
      setPrefs(await updatePreferences(changes));
    } catch (err) {
      setPrefs(previous);
      setSaveError(err instanceof Error ? err.message : "Could not save your change. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const toggleAll = (receiveEmails) =>
    save({ ...prefs, unsubscribedAll: !receiveEmails }, { unsubscribedAll: !receiveEmails });

  const toggleLeague = (leagueId, field, value) =>
    save(
      {
        ...prefs,
        leagues: prefs.leagues.map((l) => (l.leagueId === leagueId ? { ...l, [field]: value } : l)),
      },
      { leagues: [{ leagueId, [field]: value }] }
    );

  if (loadError) return <Alert severity="error">{loadError}</Alert>;
  if (!prefs) return <Typography variant="body2">Loading email settings...</Typography>;

  const emailOff = prefs.unsubscribedAll;

  return (
    <Stack spacing={2} sx={{ maxWidth: 700 }}>
      <Typography variant="h5">Notifications</Typography>
      <Typography variant="body2" color="text.secondary">
        Choose how you hear from Puck Pickup. These settings only change what you are sent: you stay registered for your
        leagues and games either way.
      </Typography>

      {/* Each way of reaching someone gets its own sub header, so others (e.g. GroupMe) can sit alongside Email. */}
      <Stack component="section" aria-labelledby="notification-email-heading" spacing={2}>
      <Typography id="notification-email-heading" variant="h6" component="h3">
        Email
      </Typography>

      {saveError ? (
        <Alert severity="error" onClose={() => setSaveError("")}>
          {saveError}
        </Alert>
      ) : null}

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={0.5}>
            <FormControlLabel
              control={
                <Switch
                  checked={!emailOff}
                  disabled={saving}
                  onChange={(e) => toggleAll(e.target.checked)}
                  inputProps={{ "aria-label": "Receive emails from Puck Pickup" }}
                />
              }
              label="Receive emails from Puck Pickup"
            />
            <Typography variant="caption" color="text.secondary">
              {emailOff
                ? "All emails are off. You will still get receipts for payments you make."
                : "Turn this off to stop all emails except receipts for payments you make."}
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      {prefs.leagues.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          You are not part of any leagues yet. When you join one, you can choose its emails here.
        </Typography>
      ) : (
        prefs.leagues.map((league) => (
          <Card key={league.leagueId} variant="outlined" sx={{ opacity: emailOff ? 0.6 : 1 }}>
            <CardContent>
              <Stack spacing={1}>
                <Typography variant="subtitle1" component="h4" sx={{ fontWeight: 600 }}>
                  {league.leagueName}
                </Typography>
                {LEAGUE_TOGGLES.map(({ field, label, hint }) => (
                  <Stack key={field} spacing={0}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={league[field]}
                          disabled={saving || emailOff}
                          onChange={(e) => toggleLeague(league.leagueId, field, e.target.checked)}
                          inputProps={{ "aria-label": `${label} for ${league.leagueName}` }}
                        />
                      }
                      label={label}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ pl: 6 }}>
                      {hint}
                    </Typography>
                  </Stack>
                ))}
                <Typography variant="caption" color="text.secondary">
                  You will always get a notice if a game you are signed up for is cancelled, and receipts for payments you make.
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        ))
      )}
      </Stack>
    </Stack>
  );
}
